import React, { useEffect, useState } from "react";
import { Box, Grid } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import Loader from "../../components/Molecules/Loader/Loader";
import { useBreadCrumbContext } from "../../contexts/BreadCrumbContext";
import { useAuth } from "../../contexts/AuthContext";
import { getComparisonRunScoringConfigById } from "../../services/scoringService";
import { getActiveRunInstance } from "../../services/runInstance";
import { getRulesList } from "../../services/rulesService";
import { IconBtnEditBase } from "../../common/utils/icons";

const ViewComparisonRunScoring: React.FC = () => {
  const { id: configId } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission("comparison_run_scoring_config", "edit");
  const { setCurrentBreadcrumbPage } = useBreadCrumbContext();
  const [isLoading, setIsLoading] = useState(false);
  const [configData, setConfigData] = useState<any>(null);
  const [runMap, setRunMap] = useState<Map<number, string>>(new Map());
  const [ruleMap, setRuleMap] = useState<Map<number, string>>(new Map());

  // ── Build lookup maps on mount ──────────────────────────────────────────
  useEffect(() => {
    getActiveRunInstance()
      .then((runs) => {
        if (Array.isArray(runs)) {
          const m = new Map<number, string>();
          runs.forEach((r: any) => {
            if (r.id != null) m.set(r.id, r.run_name);
          });
          setRunMap(m);
        }
      })
      .catch(() => {});

    getRulesList({ is_active: true })
      .then((rules) => {
        if (Array.isArray(rules)) {
          const m = new Map<number, string>();
          rules.forEach((r: any) => {
            if (r.id != null) m.set(r.id, r.name);
          });
          setRuleMap(m);
        }
      })
      .catch(() => {});
  }, []);

  // ── Always fetch fresh data from API ────────────────────────────────────
  useEffect(() => {
    if (!configId) return;
    const numericId = Number(configId);
    setIsLoading(true);
    getComparisonRunScoringConfigById(numericId)
      .then((d) => {
        if (!d) return;
        if (Array.isArray(d)) {
          const found = d.find((item: any) => item.id === numericId) || d[0];
          if (found) setConfigData(found);
        } else {
          setConfigData(d);
        }
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, [configId]);

  useEffect(() => {
    setCurrentBreadcrumbPage({
      name: `Config #${configId}`,
      id: configId ? parseInt(configId) : null,
    });
    return () => setCurrentBreadcrumbPage({ name: "", id: null });
  }, [configId]);

  const field = (label: string, value: any, md: number = 4) => (
    <Grid item xs={12} sm={6} md={md}>
      <label className="label-text">{label}</label>
      <div className="form-control">{value ?? "—"}</div>
    </Grid>
  );

  const runName = configData?.run_instance_id
    ? (runMap.get(configData.run_instance_id) ??
      `Run #${configData.run_instance_id}`)
    : null;

  const ruleName = configData?.rule_id
    ? (ruleMap.get(configData.rule_id) ?? `Rule #${configData.rule_id}`)
    : null;

  return (
    <Box>
      {isLoading && <Loader isLoading={isLoading} />}
      <Box className="text-box-card-white">
        <Box className="text-box-header">
          <h3>View Comparison Run Scoring Config</h3>
        </Box>
        <Grid
          container
          rowSpacing={2.5}
          columnSpacing={{ xl: 4, lg: 3, md: 2, sm: 2, xs: 1 }}
        >
          {field("Execution Group Name", runName)}
          {field("Rule Name", ruleName)}
          {canEdit && (
            <Grid
              item
              xs={12}
              sm={6}
              md
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
              }}
            >
              <button
                className="btn-nostyle icon-btn-edit"
                onClick={() =>
                  navigate(`/scoring/comparison-run/edit/${configId}`)
                }
              >
                <Box className="d-block">
                  <IconBtnEditBase ariaLabel="Edit Comparison Run Scoring Config" />
                </Box>
              </button>
            </Grid>
          )}
        </Grid>
        <Grid
          container
          rowSpacing={2.5}
          columnSpacing={{ xl: 4, lg: 3, md: 2, sm: 2, xs: 1 }}
          sx={{ mt: "20px" }}
        >
          {field("Severity", configData?.severity)}
          {field("Factor", configData?.factor)}
        </Grid>
      </Box>
    </Box>
  );
};

export default ViewComparisonRunScoring;
